import type { DateTime } from 'luxon'

import assertDefined from '@/utils/assertDefined'

import { useUser } from '@/client/context/user'

import type { PostDetails } from '@/components/Community/Post/types'
import Alert from '@/components/UI/Alert'
import type { LiveHelpRequestWithNullableSessionDetails } from '@/components/Worlds/types'

import { isLiveHelpRequestWithSessionDetails } from './helpers'
import TutorGivingLiveHelpBanner from './TutorGivingLiveHelpBanner'
import TutorLearnerBeingHelpedBanner from './TutorLearnerBeingHelpedBanner'
import TutorRequestedLiveHelpBanner from './TutorRequestedLiveHelpBanner'

const DATE_FORMAT = 'LLL d, y'

export default function TutorLiveHelpBanner({
  post,
  liveHelpRequest,
  currentUserLastHelpedLearnerAt,
  postIsFullView,
}: {
  post: PostDetails
  liveHelpRequest: LiveHelpRequestWithNullableSessionDetails
  currentUserLastHelpedLearnerAt: DateTime | null
  postIsFullView: boolean
}): JSX.Element | null {
  const currentUser = useUser()

  const { user } = post
  const { liveHelpInfo } = post

  if (liveHelpInfo == null) {
    return null
  }

  const { postStatus } = liveHelpInfo

  if (postStatus === 'solved') {
    assertDefined(currentUserLastHelpedLearnerAt, 'Unexpected state')

    const liveHelpDateString = currentUserLastHelpedLearnerAt.toFormat(DATE_FORMAT)

    return (
      <Alert bg="bg.success" color="fg.success">
        You helped {user.displayName} over Zoom on {liveHelpDateString}
      </Alert>
    )
  }

  if (!isLiveHelpRequestWithSessionDetails(liveHelpRequest)) {
    return (
      <TutorRequestedLiveHelpBanner
        post={post}
        liveHelpRequest={liveHelpRequest}
        postIsFullView={postIsFullView}
      />
    )
  }

  const { host } = liveHelpRequest.session

  if (host.user.id === currentUser.id) {
    return (
      <TutorGivingLiveHelpBanner
        post={post}
        liveHelpRequest={liveHelpRequest}
        postIsFullView={postIsFullView}
      />
    )
  }

  return <TutorLearnerBeingHelpedBanner learner={user} tutor={host.user} />
}
